/**
 * Sync Job Cancel API Contract
 *
 * PATCH /v1/system/sync-jobs/:sync_job_public_id — cancel one sync job
 *
 * Cancel-only (plan 2026-10-03-1418 KTD11). `status` must be `'CANCELLED'`; any
 * other status, or none, is a 400. Other keys are dropped rather than rejected
 * on purpose: during a deploy, an older dashboard still sends `error` alongside
 * the cancel, and refusing it would break cancel until the web catches up. The
 * cancel goes through the Job Ledger and applies only while the job is PENDING
 * or IN_PROGRESS.
 *
 * When the job has already finished, the endpoint answers 409 with error type
 * {@link SYNC_JOB_CANCEL_CONFLICT_TYPE} and `error.details.currentStatus`, so
 * the dashboard can say "already finished" instead of reporting a cancel that
 * did not happen.
 *
 * Cancelling an IN_PROGRESS job does not stop its worker; the worker's own
 * finish then loses. Child jobs are not cancelled with their parent.
 */

import { z } from 'zod'

export const CancelSyncJobBodySchema = z.object({
  status: z.literal('CANCELLED')
})

export type CancelSyncJobBody = z.infer<typeof CancelSyncJobBodySchema>

/** The `error.type` of the 409 a cancel gets when the job already finished. */
export const SYNC_JOB_CANCEL_CONFLICT_TYPE = 'SYSTEM_ALREADY_COMPLETED'

/** `error.details` of that 409. `currentStatus` is the status the job finished in. */
export const CancelSyncJobConflictDetailsSchema = z.object({
  currentStatus: z.string()
})

export type CancelSyncJobConflictDetails = z.infer<typeof CancelSyncJobConflictDetailsSchema>
